import { useState } from "react";
import { Line } from "react-chartjs-2";
// Components
import { MainDiv, Header, Title, Subtitle, OpenButton, OpenButtonImg } from "./styles";
import Graphic from "../../../graphic";
import NoDataComponent from "../../../noDataComponent";
// Assets
import ArrowUpImg from "../../../../../../assets/icons/ico_arrow_up_blue.svg";
import ArrowDownImg from "../../../../../../assets/icons/ico_arrow_down_blue.svg";

const WellBeingAssessmentsPlot = ({ isPositiveAnswers, emotionalAnswersPerWeek }) => {
  const [showPlot, setShowPlot] = useState(false);

  const positiveAnswersPerWeek = emotionalAnswersPerWeek.map((emotionalAnswerPerWeek) => {
    return emotionalAnswerPerWeek.filter((emotionalAnswer) => emotionalAnswer.EMOTIONAL_QUESTION_KEY === "mood");
  });

  const negativeAnswersPerWeek = emotionalAnswersPerWeek.map((emotionalAnswerPerWeek) => {
    return emotionalAnswerPerWeek.filter((emotionalAnswer) => emotionalAnswer.EMOTIONAL_QUESTION_KEY !== "mood");
  });

  const getEmotionalAnswerPerWeek = (key) => {
    const targetedEmotionalAnswers = isPositiveAnswers ? positiveAnswersPerWeek : negativeAnswersPerWeek;
    const emotionalAnswersAverage = targetedEmotionalAnswers.map((emotionalAnswer) => {
      const emotionalAnswers = emotionalAnswer.filter((emotionalAnswer) => emotionalAnswer.EMOTIONAL_QUESTION_KEY === key);
      if (!emotionalAnswers.length) return NaN;

      const emotionalAnswersSum = emotionalAnswers.reduce(
        (accumulator, emotionalAnswer) => accumulator + Number(emotionalAnswer.EMOTIONAL_ANSWER),
        0
      );

      return emotionalAnswersSum / emotionalAnswers.length;
    });

    return emotionalAnswersAverage;
  };

  const getEmotionalAnswerPerWeekAndDay = (emotionalAnswersWeek, key, day) => {
    const emotionalAnswers = emotionalAnswersWeek.filter(
      (emotionalAnswer) => emotionalAnswer.EMOTIONAL_QUESTION_KEY === key && emotionalAnswer.DAY === day
    );

    if (!emotionalAnswers.length) return 0;

    const emotionalAnswersSum = emotionalAnswers.reduce(
      (accumulator, emotionalAnswer) => accumulator + Number(emotionalAnswer.EMOTIONAL_ANSWER),
      0
    );

    return emotionalAnswersSum / emotionalAnswers.length;
  };

  const negativeAnswersData = {
    labels: emotionalAnswersPerWeek.map((_, i) => i + 1),
    datasets: [
      {
        label: "Pain",
        data: getEmotionalAnswerPerWeek("pain"),
        backgroundColor: ["#22B573"],
        borderColor: ["#22B573"],
      },
      {
        label: "Fatigue",
        data: getEmotionalAnswerPerWeek("energy"),
        backgroundColor: ["#FF9900"],
        borderColor: ["#FF9900"],
      },
    ],
  };

  const positiveAnswersData = {
    labels: emotionalAnswersPerWeek.map((_, i) => i + 1),
    datasets: [
      {
        label: "Happiness",
        data: getEmotionalAnswerPerWeek("mood"),
        backgroundColor: ["#1155CC"],
        borderColor: ["#1155CC"],
      },
    ],
  };

  const keyMapping = {
    Pain: "pain",
    Fatigue: "energy",
    Happiness: "mood",
  };

  const options = {
    clip: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          usePointStyle: true,
          filter: (legendItem, chartData) => {
            let entries = chartData.datasets.map((e) => e.label);
            return entries.indexOf(legendItem.text) === legendItem.datasetIndex;
          },
        },
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            return `${tooltipItems[0].dataset.label} → ${tooltipItems[0].formattedValue}`;
          },
          label: (tooltipItem) => {
            const emotionalAnswers = emotionalAnswersPerWeek[tooltipItem.dataIndex];
            const key = keyMapping[tooltipItem.dataset.label];

            const monday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "MONDAY");
            const tuesday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "TUESDAY");
            const wednesday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "WEDNESDAY");
            const thursday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "THURSDAY");
            const friday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "FRIDAY");
            const saturday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "SATURDAY");
            const sunday = getEmotionalAnswerPerWeekAndDay(emotionalAnswers, key, "SUNDAY");

            return [
              `Monday → ${monday}`,
              `Tuesday → ${tuesday}`,
              `Wednesday → ${wednesday}`,
              `Thursday → ${thursday}`,
              `Friday → ${friday}`,
              `Saturday → ${saturday}`,
              `Sunday → ${sunday}`,
            ];
          },
        },
      },
    },
  };

  const plugin = {
    id: "highlightDrops",
    beforeDraw: (chart) => {
      const { ctx, chartArea, data } = chart;
      const metaSets = chart._metasets;
      const numPoints = data.labels.length;

      ctx.save();

      for (let i = 1; i < numPoints; i++) {
        let sumPrev = 0;
        let countPrev = 0;
        let sumCurr = 0;
        let countCurr = 0;

        for (let datasetIndex = 0; datasetIndex < data.datasets.length; datasetIndex++) {
          const values = data.datasets[datasetIndex].data;

          const prevVal = values[i - 1];
          const currVal = values[i];

          if (prevVal !== null && !isNaN(prevVal)) {
            sumPrev += prevVal;
            countPrev++;
          }
          if (currVal !== null && !isNaN(currVal)) {
            sumCurr += currVal;
            countCurr++;
          }
        }

        if (countPrev > 0 && countCurr > 0) {
          const avgPrev = sumPrev / countPrev;
          const avgCurr = sumCurr / countCurr;

          if (isPositiveAnswers && avgCurr < avgPrev) {
            const xStart = metaSets[0].data[i - 1].x;
            const xEnd = metaSets[0].data[i].x;

            ctx.fillStyle = "rgba(241, 90, 36, 0.1)";
            ctx.fillRect(xStart, chartArea.top, xEnd - xStart, chartArea.bottom - chartArea.top);
          }

          if (!isPositiveAnswers && avgCurr > avgPrev) {
            const xStart = metaSets[0].data[i - 1].x;
            const xEnd = metaSets[0].data[i].x;

            ctx.fillStyle = "rgba(241, 90, 36, 0.1)";
            ctx.fillRect(xStart, chartArea.top, xEnd - xStart, chartArea.bottom - chartArea.top);
          }
        }
      }

      ctx.restore();
    },
  };

  const checkSomeEmotionalAnswer = () => emotionalAnswersPerWeek.some((subArray) => subArray.length > 0);

  return (
    <MainDiv>
      <Header>
        <Title>
          Self Reported <Subtitle>{isPositiveAnswers ? "Happiness" : "Pain and Fatigue"}</Subtitle>
        </Title>
        <OpenButton onClick={() => setShowPlot(!showPlot)}>
          <OpenButtonImg src={showPlot ? ArrowUpImg : ArrowDownImg} alt="arrow-ico" />
        </OpenButton>
      </Header>
      {showPlot &&
        (checkSomeEmotionalAnswer() ? (
          <Graphic
            Title={""}
            Type={Line}
            data={isPositiveAnswers ? positiveAnswersData : negativeAnswersData}
            options={options}
            axisY={"Metrics"}
            axisX={"Weeks"}
            max={10}
            plugin={plugin}
          />
        ) : (
          <NoDataComponent text="There are no reports" />
        ))}
    </MainDiv>
  );
};

export default WellBeingAssessmentsPlot;
